import { X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ImageWithLoader } from '@/components/ui/image-with-loader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { getAssetUrl, getAssetById } from '@/modules/case/utils/asset-loader';
import { SpeakerVideo } from '@/modules/speaker-video';

import { ZoomableImage } from './ZoomableImage';
import defaultConfig from '../config/viewer-config.json';
import { useAssetViewer } from '../hooks/useAssetViewer';
import { AssetViewerProps, AssetViewerConfig } from '../types';
import { setupKeyboardNavigation } from '../utils/navigation';

export const AssetViewer = ({
  isOpen,
  onClose,
  assets,
  assetsBaseUrl,
  initialIndex = 0,
  title,
  customContent,
  config: configOverride,
}: AssetViewerProps) => {
  // Merge default config with overrides
  const config = {
    ...defaultConfig,
    ...configOverride,
    navigation: { ...defaultConfig.navigation, ...configOverride?.navigation },
    display: { ...defaultConfig.display, ...configOverride?.display },
    speakerVideo: {
      ...defaultConfig.speakerVideo,
      ...configOverride?.speakerVideo,
    },
    accessibility: {
      ...defaultConfig.accessibility,
      ...configOverride?.accessibility,
    },
    transitions: {
      // NOTE: It is ok to cast here because we don't have a choice to transform the json transition type
      // to the correct type
      ...(defaultConfig.transitions as AssetViewerConfig['transitions']),
      ...configOverride?.transitions,
    },
  } satisfies AssetViewerConfig;

  const {
    currentIndex,
    speakerVideoUrl,
    setSpeakerVideoUrl,
    thumbnailRefs,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    swipeOffset,
    setSwipeOffset,
    isTransitioning,
    setIsTransitioning,
    isVerticalSwipe,
    setIsVerticalSwipe,
    swipeDelta,
    isImageZoomed,
    setIsImageZoomed,
  } = useAssetViewer(assets, initialIndex, isOpen, config);

  // Setup swipe handlers
  const handlers = useSwipeable({
    onSwiping: (eventData) => {
      if (customContent || assets.length <= 1 || isTransitioning || isImageZoomed) return;

      const { deltaX, deltaY } = eventData;

      // Detect vertical swipe
      if (Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
        if (!isVerticalSwipe) {
          setIsVerticalSwipe(true);
        }
        return;
      }

      if (isVerticalSwipe) return;

      // Apply visual effect with resistance at edges
      let newOffset = deltaX;

      if (currentIndex === 0 && deltaX > 0) {
        newOffset = deltaX * 0.5;
      }
      if (currentIndex === assets.length - 1 && deltaX < 0) {
        newOffset = deltaX * 0.5;
      }

      setSwipeOffset(newOffset);
    },

    onSwipedLeft: (eventData) => {
      if (
        customContent ||
        assets.length <= 1 ||
        isTransitioning ||
        isVerticalSwipe ||
        isImageZoomed
      ) {
        setSwipeOffset(0);
        setIsVerticalSwipe(false);
        return;
      }

      const { deltaX } = eventData;

      if (Math.abs(deltaX) >= swipeDelta) {
        setIsTransitioning(true);
        handleNext();
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
      }

      setSwipeOffset(0);
      setIsVerticalSwipe(false);
    },

    onSwipedRight: (eventData) => {
      if (
        customContent ||
        assets.length <= 1 ||
        isTransitioning ||
        isVerticalSwipe ||
        isImageZoomed
      ) {
        setSwipeOffset(0);
        setIsVerticalSwipe(false);
        return;
      }

      const { deltaX } = eventData;

      if (Math.abs(deltaX) >= swipeDelta) {
        setIsTransitioning(true);
        handlePrevious();
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
      }

      setSwipeOffset(0);
      setIsVerticalSwipe(false);
    },

    onSwiped: () => {
      setSwipeOffset(0);
      setIsVerticalSwipe(false);
    },

    delta: swipeDelta,
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: false,
  });

  // Setup keyboard navigation
  useEffect(() => {
    return setupKeyboardNavigation(config, isOpen, handlePrevious, handleNext, onClose);
  }, [isOpen, config, handlePrevious, handleNext, onClose]);

  // Allow rendering if customContent is provided, even without assets
  if (!customContent && (!assets || assets.length === 0)) return null;

  const currentAsset = assets[currentIndex];
  const assetTitle = currentAsset?.title || title;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="!left-0 !top-0 w-screen max-w-none !translate-x-0 !translate-y-0 rounded-none border-0 bg-black/95 p-0"
        style={{ height: '100dvh' }}
        hideDefaultClose
      >
        {/* DialogTitle for accessibility - visually hidden since we have a visible title in header */}
        <DialogTitle className="sr-only">{assetTitle || 'Asset Viewer'}</DialogTitle>
        {/* Header with title, counter and close button */}
        <div className="absolute left-0 right-0 top-0 z-50 flex items-center justify-between p-3 sm:p-4 md:p-6">
          <div className="flex items-center gap-2 sm:gap-4">
            {config.display.showTitle && assetTitle && (
              <h2 className="max-w-[200px] truncate font-heading text-lg font-semibold text-white sm:max-w-none sm:text-xl md:text-2xl">
                {assetTitle}
              </h2>
            )}
            {config.display.showCounter && !customContent && assets.length > 0 && (
              <span className="font-body text-xs text-white/70 sm:text-sm">
                {currentIndex + 1}/{assets.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-full bg-white/10 p-1.5 transition-colors hover:bg-white/20 sm:p-2"
            aria-label={config.accessibility.ariaLabels.close}
          >
            <X className="h-5 w-5 text-white sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Left navigation */}
        {config.navigation.enableArrowButtons && !customContent && assets.length > 1 && (
          <button
            onClick={handlePrevious}
            className="absolute left-2 top-1/2 z-50 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-livelinx-blue300 text-white shadow-lg transition-all hover:scale-110 hover:bg-livelinx-blue400 sm:left-4 sm:h-12 sm:w-12 lg:left-6"
            aria-label={config.accessibility.ariaLabels.previous}
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        )}

        {/* Main content */}
        {customContent ? (
          <div {...handlers} className="absolute inset-0 mt-16 overflow-y-auto overflow-x-hidden">
            <div className="flex w-full items-start justify-center py-4">
              <div className="w-full max-w-4xl [&>div]:!h-auto">{customContent}</div>
            </div>
          </div>
        ) : (
          <div
            {...handlers}
            className="absolute inset-0 flex items-center justify-center overflow-hidden px-2 pb-32 pt-16 sm:px-4 sm:pb-28 sm:pt-20 md:px-8 md:pb-32 lg:px-12 xl:px-20"
            style={{
              transform: `translateX(${swipeOffset}px)`,
              transition: isTransitioning ? 'transform 0.3s ease-out' : 'none',
            }}
          >
            <>
              {currentAsset?.type === 'video' ? (
                <video
                  key={getAssetUrl(currentAsset, assetsBaseUrl)}
                  controls
                  autoPlay={config.display.autoPlayVideos}
                  className="h-full w-full object-contain"
                  src={getAssetUrl(currentAsset, assetsBaseUrl)}
                />
              ) : (
                <ZoomableImage
                  src={getAssetUrl(currentAsset, assetsBaseUrl)}
                  alt={currentAsset?.title || `Asset ${currentIndex + 1}`}
                  containerClassName="w-full h-full"
                  onZoomChange={setIsImageZoomed}
                />
              )}
            </>
          </div>
        )}

        {/* Speaker video bubble overlay */}
        {config.speakerVideo.enabled && speakerVideoUrl && (
          <SpeakerVideo
            videoUrl={speakerVideoUrl}
            onVideoEnd={() => setSpeakerVideoUrl(null)}
            position={config.speakerVideo.position as any}
          />
        )}

        {/* Right navigation */}
        {config.navigation.enableArrowButtons && !customContent && assets.length > 1 && (
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 z-50 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-livelinx-blue300 text-white shadow-lg transition-all hover:scale-110 hover:bg-livelinx-blue400 sm:right-4 sm:h-12 sm:w-12 lg:right-6"
            aria-label={config.accessibility.ariaLabels.next}
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        )}

        {/* Thumbnail bar */}
        {config.navigation.enableThumbnails && !customContent && assets.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 h-28 border-t border-white/10 bg-black/60 backdrop-blur-sm sm:h-24 md:h-28 lg:h-32">
            <ScrollArea className="h-full">
              <div className="flex h-full items-center justify-center gap-2 px-3 py-3 sm:gap-3 sm:px-6 sm:py-4">
                {assets.slice(0, config.display.maxThumbnails).map((asset, index) => (
                  <div
                    key={asset.id}
                    ref={(el) => (thumbnailRefs.current[index] = el)}
                    className="relative flex-shrink-0"
                  >
                    <div
                      onClick={() => handleThumbnailClick(index)}
                      className={cn(
                        'cursor-pointer overflow-hidden rounded-lg transition-all',
                        index === currentIndex
                          ? 'scale-110 border-4 border-primary shadow-lg'
                          : 'border-2 border-white/20 opacity-70 hover:scale-105 hover:border-white/50 hover:opacity-100'
                      )}
                      style={{
                        width: `${config.display.thumbnailSize.width}px`,
                        height: `${config.display.thumbnailSize.height}px`,
                      }}
                    >
                      {asset.type === 'video' ? (
                        <video
                          src={getAssetUrl(asset, assetsBaseUrl)}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <ImageWithLoader
                          src={getAssetUrl(asset, assetsBaseUrl)}
                          alt={asset.title}
                          className="h-full w-full object-cover"
                          containerClassName="w-full h-full"
                          skeletonClassName="w-full h-full"
                          loading="lazy"
                        />
                      )}
                    </div>

                    {/* Speaker video badge on thumbnail */}
                    {asset.speakerVideoRef && (
                      <div
                        className="absolute -right-1 -top-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-primary shadow-md transition-transform hover:scale-110"
                        onClick={(e) => {
                          e.stopPropagation();
                          const videoAsset = getAssetById(asset.speakerVideoRef!, assets);
                          if (videoAsset) {
                            setSpeakerVideoUrl(getAssetUrl(videoAsset, assetsBaseUrl));
                          }
                        }}
                        aria-label={config.accessibility.ariaLabels.playVideo}
                      >
                        <Play className="ml-0.5 h-3 w-3 fill-white text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
